<template>
  <v-dialog :value="show" max-width="720" @input="(v) => !v && $emit('close')">
    <v-card class="qad">
      <div class="qad__head">
        <div>
          <div class="qad__title">
            <v-icon small color="#7b5cd6" class="mr-1">{{ action.type === 'shopify' ? 'mdi-shopping-outline' : 'mdi-lightning-bolt-outline' }}</v-icon>
            {{ action.label }}
          </div>
          <div v-if="action.description" class="qad__desc">{{ action.description }}</div>
        </div>
        <v-btn icon small @click="$emit('close')"><v-icon small>mdi-close</v-icon></v-btn>
      </div>

      <div class="qad__body" v-if="refund">
        <!-- Rückerstattung: Rechenhilfe wie in Quelp (Positionen, Versand, Betrag) -->
        <div class="qad__orderBar">
          <v-icon small class="mr-1">mdi-package-variant-closed</v-icon>
          <b>{{ refund.order.name }}</b>
          <span class="qad__muted ml-2">{{ refund.order.financialStatus }}</span>
          <span class="qad__spacer"></span>
          <span>{{ money(refund.order.totalPrice) }} {{ refund.order.currency }}</span>
        </div>
        <div v-if="refund.alreadyRefunded > 0" class="qad__muted">Bereits erstattet: {{ money(refund.alreadyRefunded) }} {{ refund.order.currency }} · verbleibend {{ money(remainingRefundable) }}</div>
        <div class="qad__lines">
          <div class="qad__lineHead"><span>Artikel erstatten (optional)</span><span>Menge</span><span>Betrag</span></div>
          <div v-for="(li, i) in refund.order.lineItems" :key="i" class="qad__line">
            <div class="qad__lineTitle">
              <div>{{ li.title }}</div>
              <div class="qad__muted">{{ li.variantTitle ? li.variantTitle + ' · ' : '' }}{{ money(li.price) }} {{ refund.order.currency }} × {{ li.quantity }}</div>
            </div>
            <div class="qad__qty">
              <v-btn icon x-small :disabled="quantities[i] <= 0" @click="setQty(i, quantities[i] - 1)"><v-icon small>mdi-minus</v-icon></v-btn>
              <span class="qad__qtyVal">{{ quantities[i] }}</span>
              <v-btn icon x-small :disabled="quantities[i] >= li.quantity" @click="setQty(i, quantities[i] + 1)"><v-icon small>mdi-plus</v-icon></v-btn>
            </div>
            <div class="qad__lineAmount">{{ quantities[i] > 0 ? money(quantities[i] * effectivePrice(li)) : '–' }}</div>
          </div>
          <div v-for="(sl, i) in refund.order.shippingLines" :key="'s' + i" class="qad__line">
            <div class="qad__lineTitle"><div>{{ sl.title || 'Versand' }}</div><div class="qad__muted">{{ money(sl.price) }} {{ refund.order.currency }}</div></div>
            <div class="qad__qty"><v-simple-checkbox :value="refundShipping[i]" color="#7b5cd6" @input="toggleShipping(i)" /></div>
            <div class="qad__lineAmount">{{ refundShipping[i] ? money(sl.price) : '–' }}</div>
          </div>
        </div>
        <div class="qad__amountBar">
          <span class="qad__amountLabel">Rückerstattung</span>
          <v-text-field dense outlined hide-details type="number" step="0.01" min="0" class="qad__amountInput" :value="amountDisplay" @input="onAmountInput" :suffix="refund.order.currency" />
        </div>
        <div v-if="amountEdited" class="qad__muted">Betrag manuell gesetzt. <a @click="amountEdited = false">Wieder berechnen</a></div>
        <div class="qad__field">
          <div class="qad__label">Grund (optional)</div>
          <v-textarea dense outlined hide-details auto-grow rows="2" v-model="values.reason" placeholder="z.B. Artikel beschädigt" />
        </div>
        <v-switch dense hide-details inset color="#7b5cd6" label="Kunden benachrichtigen" :input-value="values.notify !== 'false'" @change="(v) => $set(values, 'notify', v ? 'true' : 'false')" />
        <div v-if="error" class="qad__error">{{ error }}</div>
        <div v-if="done" class="qad__ok"><v-icon small color="#2e7d32">mdi-check-circle</v-icon> Erstattet und am Ticket protokolliert.</div>
      </div>
      <div class="qad__body" v-else>
        <div v-for="f in action.fields" :key="f.name" class="qad__field">
          <!-- Array-Builder: Items wählen + Felder je Item -->
          <template v-if="f.type === 'array-builder' && f.arrayBuilder">
            <div class="qad__label">{{ f.description || f.name }}<span v-if="f.required" class="qad__req">*</span></div>
            <div v-if="!f.arrayBuilder.items.length" class="qad__muted">Keine Einträge verfügbar.</div>
            <div v-for="it in f.arrayBuilder.items" :key="it.uid" class="qad__item" :class="{ 'qad__item--on': isSelected(f.name, it.uid) }">
              <label class="qad__itemHead">
                <v-simple-checkbox :value="isSelected(f.name, it.uid)" color="#7b5cd6" @input="toggleItem(f, it)" />
                <div v-if="it.imageUrl" class="qad__thumb" :style="{ backgroundImage: `url('${it.imageUrl}')` }" @click.prevent="openImage(it.imageUrl)"></div>
                <span class="qad__itemLabel">{{ it.label || it.uid }}</span>
              </label>
              <div v-if="isSelected(f.name, it.uid)" class="qad__itemFields">
                <div v-for="itf in f.arrayBuilder.itemFields" :key="itf.outputKey" class="qad__itemField">
                  <span class="qad__itemFieldLabel">{{ itf.label || itf.outputKey }}</span>
                  <v-select v-if="itf.type === 'select'" dense outlined hide-details :items="itf.options" item-text="label" item-value="value" :value="itemValue(f.name, it.uid, itf.outputKey)" @input="(v) => setItemValue(f.name, it.uid, itf.outputKey, v)" />
                  <v-select v-else-if="itf.type === 'multiselect'" dense outlined hide-details multiple chips small-chips :items="itf.options" item-text="label" item-value="value" :value="itemValue(f.name, it.uid, itf.outputKey)" @input="(v) => setItemValue(f.name, it.uid, itf.outputKey, v)" />
                  <v-switch v-else-if="itf.type === 'boolean'" dense hide-details inset color="#7b5cd6" :input-value="String(itemValue(f.name, it.uid, itf.outputKey)) === 'true'" @change="(v) => setItemValue(f.name, it.uid, itf.outputKey, v ? 'true' : 'false')" />
                  <v-text-field v-else dense outlined hide-details :type="itf.type === 'number' ? 'number' : 'text'" :value="itemValue(f.name, it.uid, itf.outputKey)" @input="(v) => setItemValue(f.name, it.uid, itf.outputKey, v)" />
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="qad__label">{{ f.description || f.name }}<span v-if="f.required" class="qad__req">*</span><span class="qad__key">{{ f.name }}</span></div>
            <v-select v-if="f.type === 'select'" dense outlined hide-details :items="f.options || []" item-text="label" item-value="value" v-model="values[f.name]" />
            <v-switch v-else-if="f.type === 'boolean'" dense hide-details inset color="#7b5cd6" :input-value="String(values[f.name]) === 'true'" @change="(v) => $set(values, f.name, v ? 'true' : 'false')" />
            <v-textarea v-else-if="isLong(f)" dense outlined hide-details auto-grow rows="2" v-model="values[f.name]" />
            <v-text-field v-else dense outlined hide-details :type="f.type === 'number' ? 'number' : 'text'" v-model="values[f.name]" />
          </template>
        </div>
        <div v-if="error" class="qad__error">{{ error }}</div>
        <div v-if="done" class="qad__ok"><v-icon small color="#2e7d32">mdi-check-circle</v-icon> Ausgeführt und am Ticket protokolliert.</div>
      </div>

      <div class="qad__foot">
        <v-btn text small @click="$emit('close')">{{ done ? 'Schließen' : 'Abbrechen' }}</v-btn>
        <v-btn v-if="!done" small depressed rounded color="primary" :loading="busy" :disabled="!valid" @click="run">
          <v-icon small left>{{ refund ? 'mdi-cash-refund' : 'mdi-play' }}</v-icon>{{ refund ? 'Erstatten' : 'Ausführen' }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
// Generisches Formular aus dem Quelp-Aktionskatalog (GET /v1/tickets/{id}/actions).
// Ausführung läuft über Quelp (POST …/actions) und wird dort am Ticket protokolliert.
export default {
  name: "QuelpActionDialog",
  props: {
    show: { type: Boolean, default: false },
    action: { type: Object, required: true },
    execute: { type: Function, required: true }, // (actionId, fields) => Promise
  },
  data() {
    return { values: {}, selections: {}, busy: false, error: "", done: false, quantities: [], refundShipping: [], manualAmount: "", amountEdited: false };
  },
  computed: {
    refund() {
      const c = this.action.context;
      return c && c.ui === "refund" && c.order ? c : null;
    },
    remainingRefundable() {
      if (!this.refund) return 0;
      return Math.max(0, this.num(this.refund.order.totalPrice) - (this.refund.alreadyRefunded || 0));
    },
    calculatedTotal() {
      if (!this.refund) return 0;
      const items = this.refund.order.lineItems.reduce((sum, li, i) => sum + (this.quantities[i] || 0) * this.effectivePrice(li), 0);
      const ship = this.refund.order.shippingLines.reduce((sum, sl, i) => sum + (this.refundShipping[i] ? this.num(sl.price) : 0), 0);
      return items + ship;
    },
    refundAmount() {
      return this.amountEdited ? this.num(this.manualAmount) : this.calculatedTotal;
    },
    amountDisplay() {
      return this.amountEdited ? this.manualAmount : this.refundAmount.toFixed(2);
    },
    valid() {
      if (this.refund) return this.refundAmount > 0;
      return (this.action.fields || []).every((f) => {
        if (!f.required) return true;
        if (f.type === "array-builder") return (this.selections[f.name] || []).length > 0;
        return String(this.values[f.name] ?? "").trim() !== "";
      });
    },
  },
  watch: {
    action: { immediate: true, handler: "reset" },
  },
  methods: {
    reset() {
      const values = {};
      const selections = {};
      (this.action.fields || []).forEach((f) => {
        if (f.type === "array-builder") selections[f.name] = [];
        else values[f.name] = f.defaultValue ?? "";
      });
      this.values = values;
      this.selections = selections;
      this.error = "";
      this.done = false;
      const r = this.action.context && this.action.context.ui === "refund" ? this.action.context : null;
      this.quantities = r ? r.order.lineItems.map(() => 0) : [];
      this.refundShipping = r ? r.order.shippingLines.map(() => false) : [];
      this.manualAmount = "";
      this.amountEdited = false;
      if (r) {
        if (!("reason" in values)) this.$set(this.values, "reason", "");
        if (!("notify" in values)) this.$set(this.values, "notify", "true");
      }
    },
    num(v) {
      return parseFloat(String(v ?? "0").replace(/[^\d.,-]/g, "").replace(",", ".")) || 0;
    },
    money(v) {
      return this.num(v).toFixed(2);
    },
    effectivePrice(li) {
      const qty = Number(li.quantity) || 1;
      return (this.num(li.price) * qty - this.num(li.totalDiscount)) / qty;
    },
    setQty(i, val) {
      const max = Number(this.refund.order.lineItems[i].quantity) || 0;
      this.$set(this.quantities, i, Math.max(0, Math.min(val, max)));
      this.amountEdited = false;
    },
    toggleShipping(i) {
      this.$set(this.refundShipping, i, !this.refundShipping[i]);
      this.amountEdited = false;
    },
    onAmountInput(v) {
      this.manualAmount = String(v);
      this.amountEdited = true;
    },
    isLong(f) {
      return f.type === "string" && /message|note|body|text|nachricht|grund|reason/i.test(f.name);
    },
    isSelected(field, uid) {
      return (this.selections[field] || []).some((s) => s.uid === uid);
    },
    toggleItem(f, it) {
      const list = this.selections[f.name] || [];
      if (list.some((s) => s.uid === it.uid)) {
        this.$set(this.selections, f.name, list.filter((s) => s.uid !== it.uid));
      } else {
        this.$set(this.selections, f.name, [...list, { uid: it.uid, values: { ...(it.values || {}) } }]);
      }
    },
    itemValue(field, uid, key) {
      const s = (this.selections[field] || []).find((x) => x.uid === uid);
      return s ? s.values[key] : "";
    },
    setItemValue(field, uid, key, val) {
      const list = (this.selections[field] || []).map((s) => (s.uid === uid ? { ...s, values: { ...s.values, [key]: val } } : s));
      this.$set(this.selections, field, list);
    },
    openImage(url) {
      window.open(url, "_blank");
    },
    async run() {
      this.busy = true;
      this.error = "";
      try {
        const fields = { ...this.values };
        Object.keys(this.selections).forEach((k) => (fields[k] = { selections: this.selections[k] }));
        if (this.refund) {
          fields.orderId = this.refund.order.id;
          fields.amount = this.refundAmount.toFixed(2);
          fields.notify = this.values.notify !== "false" ? "true" : "false";
          const restock = this.refund.order.lineItems
            .map((li, i) => ({ title: li.title, quantity: this.quantities[i] || 0 }))
            .filter((x) => x.quantity > 0);
          if (restock.length) fields.restockItems = JSON.stringify(restock);
        }
        await this.execute(this.action.id, fields);
        this.done = true;
        this.$emit("executed");
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.qad {
  border-radius: 14px !important;
  &__head { display: flex; justify-content: space-between; align-items: flex-start; padding: 14px 16px 8px; }
  &__title { font-weight: 800; font-size: 15px; color: #282828; display: flex; align-items: center; }
  &__desc { font-size: 12px; color: rgba(0, 0, 0, 0.55); margin-top: 2px; }
  &__body { padding: 4px 16px 12px; display: flex; flex-direction: column; gap: 12px; max-height: 65vh; overflow-y: auto; }
  &__field { display: flex; flex-direction: column; gap: 4px; }
  &__label { font-size: 12px; font-weight: 600; color: rgba(0, 0, 0, 0.7); display: flex; align-items: center; gap: 6px; }
  &__req { color: #c62828; }
  &__key { font-size: 10px; color: rgba(0, 0, 0, 0.4); font-weight: 400; margin-left: auto; }
  &__muted { font-size: 12px; color: rgba(0, 0, 0, 0.5); }
  &__item { border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 10px; padding: 6px 10px; display: flex; flex-direction: column; gap: 6px;
    &--on { border-color: rgba(123, 92, 214, 0.45); background: #faf9fe; } }
  &__itemHead { display: flex; align-items: center; gap: 8px; cursor: pointer; }
  &__thumb { width: 44px; height: 44px; border-radius: 8px; background: #f1f0f5 center/cover no-repeat; }
  &__itemLabel { font-size: 12px; font-weight: 600; color: #282828; word-break: break-all; }
  &__itemFields { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 8px; padding-left: 32px; }
  &__itemField { display: flex; flex-direction: column; gap: 2px; }
  &__itemFieldLabel { font-size: 11px; color: rgba(0, 0, 0, 0.55); }
  &__error { font-size: 12px; color: #c62828; }
  &__orderBar { display: flex; align-items: center; gap: 4px; background: #f7f6fb; border-radius: 10px; padding: 8px 12px; font-size: 13px; }
  &__spacer { flex: 1 1 auto; }
  &__lines { display: flex; flex-direction: column; gap: 6px; }
  &__lineHead { display: grid; grid-template-columns: minmax(0, 1fr) 110px 80px; font-size: 11px; color: rgba(0, 0, 0, 0.5); padding: 0 4px; span:last-child { text-align: right; } span:nth-child(2) { text-align: center; } }
  &__line { display: grid; grid-template-columns: minmax(0, 1fr) 110px 80px; align-items: center; gap: 8px; padding: 6px 4px; border-bottom: 1px solid rgba(0, 0, 0, 0.05); }
  &__lineTitle { font-size: 13px; color: #282828; min-width: 0; div:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } }
  &__qty { display: flex; align-items: center; justify-content: center; gap: 4px; }
  &__qtyVal { min-width: 20px; text-align: center; font-weight: 700; }
  &__lineAmount { text-align: right; font-size: 13px; }
  &__amountBar { display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #f7f6fb; border-radius: 10px; padding: 8px 12px; }
  &__amountLabel { font-weight: 700; font-size: 13px; }
  &__amountInput { max-width: 180px; }
  &__ok { font-size: 12px; color: #2e7d32; display: flex; align-items: center; gap: 4px; }
  &__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 8px 16px 14px; }
}
</style>
